import { Subject } from 'rxjs';
import { chevronDownIconSvg } from 'constants/svg-assets';

const dayFormatter = new Intl.DateTimeFormat('en', { weekday: 'short' });
const monthFormatter = new Intl.DateTimeFormat('en', { month: 'long' });
const visibleCellCount = 3;
const cellSelector = '[data-date-carousel-cell]';

/**
 * Simple horizontal date carousel — shows visibleCellCount day cells starting
 * tomorrow, with prev/next buttons paginating one day at a time. Not a port of the full
 * touch-drag GlobalResources14/Require/js/components/dateSlider/dateSlider.js (that component's
 * drag physics/infinite-card generation were judged out of scope, since the design only shows
 * static cells + chevrons, with no drag interaction).
 *
 * The selected day always advances/retreats together with startOffset (the visible window), so a
 * cell is always selected and always visible — clicking Next/Prev picks the next/previous
 * available slot rather than just panning past whatever was selected.
 */
export default class DateCarousel {
  constructor() {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    this.firstSelectableDate = new Date(today);
    this.firstSelectableDate.setDate(this.firstSelectableDate.getDate() + 1);

    this.startOffset = 0;
    this.selectedOffset = 0;
    this.id = 'nhs-date-carousel';
    this.hiddenInputId = 'nhs_date';
    this.changeSubject = new Subject();
    this.onContainerClick = null;
  }

  getDateForOffset(offset) {
    const date = new Date(this.firstSelectableDate);
    date.setDate(date.getDate() + offset);
    return date;
  }

  /**
   * Matches DateInput.js's value format (plain "YYYY-MM-DD", no time/offset) — the format
   * PlanTourLead/SpecTourLead already expect from formData.date. Built from the local date parts
   * directly (not toISOString(), which converts to UTC first and would shift the calendar day in
   * timezones east of UTC).
   */
  getDateStringForOffset(offset) {
    const date = this.getDateForOffset(offset);
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${date.getFullYear()}-${month}-${day}`;
  }

  getVisibleDates() {
    return Array.from({ length: visibleCellCount }, (_, index) => this.getDateForOffset(this.startOffset + index));
  }

  getNode() {
    return document.getElementById(this.id);
  }

  getCellsHtml() {
    return this.getVisibleDates()
      .map((date, index) => {
        const offset = this.startOffset + index;
        const isSelected = offset === this.selectedOffset;
        return `<button
          type="button"
          class="nhs-c-date-carousel__cell${isSelected ? ' nhs-c-date-carousel__cell--selected' : ''}"
          data-date-carousel-cell
          data-date-carousel-offset="${offset}"
          aria-pressed="${isSelected}"
        >
          <span class="nhs-c-date-carousel__day-name">${dayFormatter.format(date).toUpperCase().replace('.', '')}</span>
          <span class="nhs-c-date-carousel__day-number">${date.getDate()}</span>
        </button>`;
      })
      .join('');
  }

  getHtml() {
    return `
    <div id="${this.id}" class="nhs-c-date-carousel">
      <p class="nhs-c-date-carousel__month" data-date-carousel-month>${monthFormatter.format(this.getVisibleDates()[0])}</p>
      <div class="nhs-c-date-carousel__row">
        <button type="button" class="nhs-c-date-carousel__pagination" data-date-carousel-prev aria-label="Previous day" ${this.startOffset === 0 ? 'disabled' : ''}>
          <span class="nhs-c-date-carousel__pagination-icon--prev">${chevronDownIconSvg}</span>
        </button>
        <div class="nhs-c-date-carousel__cells" data-date-carousel-cells>${this.getCellsHtml()}</div>
        <button type="button" class="nhs-c-date-carousel__pagination" data-date-carousel-next aria-label="Next day">
          <span class="nhs-c-date-carousel__pagination-icon--next">${chevronDownIconSvg}</span>
        </button>
      </div>
      <input type="hidden" name="date" id="${this.hiddenInputId}" value="${this.getDateStringForOffset(this.selectedOffset)}" data-form-date-input />
    </div>`;
  }

  updateDom() {
    const node = this.getNode();
    if (!node) {
      return;
    }

    node.querySelector('[data-date-carousel-cells]').innerHTML = this.getCellsHtml();
    node.querySelector('[data-date-carousel-month]').textContent = monthFormatter.format(this.getVisibleDates()[0]);
    node.querySelector('[data-date-carousel-prev]').disabled = this.startOffset === 0;
    document.getElementById(this.hiddenInputId).value = this.getDateStringForOffset(this.selectedOffset);
  }

  /**
   * selectOffset: sets the selected day, refreshes the DOM, and notifies subscribers. The single
   * path both cell clicks and Next/Prev pagination go through, so a change always persists and a
   * cell is always shown as selected regardless of how it was chosen.
   */
  selectOffset(offset) {
    this.selectedOffset = offset;
    this.updateDom();
    this.changeSubject.next({ date: this.getDateStringForOffset(this.selectedOffset) });
  }

  onPrev() {
    if (this.startOffset === 0) {
      return;
    }

    this.startOffset -= 1;
    this.selectOffset(this.selectedOffset - 1);
  }

  onNext() {
    this.startOffset += 1;
    this.selectOffset(this.selectedOffset + 1);
  }

  onCellClick(event) {
    const cell = event.target.closest(cellSelector);
    if (!cell) {
      return;
    }

    this.selectOffset(Number(cell.dataset.dateCarouselOffset));
  }

  bindEvents() {
    this.onPrevClick = this.onPrev.bind(this);
    this.onNextClick = this.onNext.bind(this);
    this.onCellClickBound = this.onCellClick.bind(this);
    this.getNode().querySelector('[data-date-carousel-prev]').addEventListener('click', this.onPrevClick);
    this.getNode().querySelector('[data-date-carousel-next]').addEventListener('click', this.onNextClick);
    this.getNode().querySelector('[data-date-carousel-cells]').addEventListener('click', this.onCellClickBound);
  }

  unbindEvents() {
    this.getNode()?.querySelector('[data-date-carousel-prev]')?.removeEventListener('click', this.onPrevClick);
    this.getNode()?.querySelector('[data-date-carousel-next]')?.removeEventListener('click', this.onNextClick);
    this.getNode()?.querySelector('[data-date-carousel-cells]')?.removeEventListener('click', this.onCellClickBound);
  }

  /**
   * getOnChangeEvent: emits whenever the selected day changes, whether from a cell click or from
   * Next/Prev advancing the selection to keep a slot visible.
   */
  getOnChangeEvent() {
    return this.changeSubject.asObservable();
  }

  /**
   * Not required per Figma — a day is always selected by default (selectedOffset starts at 0 and
   * onPrev never lets it go negative), so this only confirms that invariant instead of checking
   * for an empty state the way the other fields do. Kept as a method, not removed, so
   * RequestATourForm can validate every field the same way.
   */
  validate() {
    return Promise.resolve(this.selectedOffset >= 0);
  }
}
